import type {BasicTextPosition, PositionDirection} from "@esWebAPI/es/model/Models";

/**
 * Calculate PositionDirection for LLL based on an attempt index.
 * Since we have 16 directions per attempt, we can use the attemptIndex to determine both direction and the distance multiplier.
 * note: previously we statically and pre-emptively created all positions but doesn't really work with points with different sizes.
 */
export class LeaderLinePositionCalculator {

    // Define all directions
    private static readonly DIRECTION_CONFIGS: Record<string, {direction: string, angle: number}> = {
        'S':   {direction: 'S',   angle: 90},
        'N':   {direction: 'N',   angle: 270},
        'E':   {direction: 'E',   angle: 0},
        'W':   {direction: 'W',   angle: 180},
        'SE':  {direction: 'SE',  angle: 45},
        'NW':  {direction: 'NW',  angle: 225},
        'SW':  {direction: 'SW',  angle: 135},
        'NE':  {direction: 'NE',  angle: 315},
        'SSE': {direction: 'SSE', angle: 60},
        'SSW': {direction: 'SSW', angle: 120},
        'WSW': {direction: 'WSW', angle: 150},
        'WNW': {direction: 'WNW', angle: 210},
        'NNW': {direction: 'NNW', angle: 240},
        'NNE': {direction: 'NNE', angle: 300},
        'ENE': {direction: 'ENE', angle: 330},
        'ESE': {direction: 'ESE', angle: 30},
    }

    // Define order for each BasicTextPosition/anchor position
    private static readonly DIRECTION_ORDERS: Record<BasicTextPosition, string[]> = {
        'BELOW': [
            'S', 'N', 'E', 'W',
            'SE', 'NW', 'SW', 'NE',
            'SSE', 'SSW', 'WSW', 'WNW', 'NNW', 'NNE', 'ENE', 'ESE'
        ],
        'CENTRAL': [
            'S', 'N', 'E', 'W',
            'SE', 'NW', 'SW', 'NE',
            'SSE', 'NNE', 'WSW', 'ESE', 'NNW', 'SSW', 'ENE', 'WNW'
        ],
        'ABOVE': [
            'N', 'S', 'E', 'W',
            'NE', 'SW', 'NW', 'SE',
            'NNE', 'NNW', 'WNW', 'WSW', 'SSW', 'SSE', 'ESE', 'ENE'
        ],
    }

    private static readonly ROTATION_BLOCK_SIZE = 4; // number of positions to skip per attempt round/iteration

    /**
     * Get direction at specified index i.e. full array iteration with wrap-around for any starting index.
     * note: this is to help avoid over-bias of southern labels since S is the first position we try in each round.
     */
    private static getDirectionAtIndex(startingIndex: number, currentIndex: number, basicTextPosition: BasicTextPosition): {direction: string, angle: number} {
        const directionOrder = this.DIRECTION_ORDERS[basicTextPosition];
        const actualIndex = (startingIndex + currentIndex) % directionOrder.length;
        const directionKey = directionOrder[actualIndex];
        return this.DIRECTION_CONFIGS[directionKey];
    }

    private static readonly DIRECTIONS_PER_ATTEMPT = 16;

    /**
     * Calculate a specific PositionDirection based on size and attemptIndex.
     * todo: compensate for zoomScaleDependentSizing featSize
     * @param featRadius featSize / 2 used as stepping distance
     * @param spacing {width: number, height: number}
     * @param attemptIndex attempt index
     * @param basicTextPosition anchor position chosen by user in styling page
     * @returns PositionDirection | null
     */
    public static getPositionForAttempt(
        featRadius: number,
        spacing: {width: number, height: number},
        attemptIndex: number,
        basicTextPosition: BasicTextPosition = 'BELOW'
    ): PositionDirection | undefined {

        // calculate which attempt round we're on e.g. 1st round of 16 directions (s, ssw, etc)
        const attemptRound = Math.floor(attemptIndex / this.DIRECTIONS_PER_ATTEMPT) + 1;

        // calculate which direction within the current attempt (0-15)
        const startingIndex = (attemptRound - 1) * this.ROTATION_BLOCK_SIZE;

        const directionIndex = attemptIndex % this.DIRECTIONS_PER_ATTEMPT;

        const directionConfig = this.getDirectionAtIndex(startingIndex, directionIndex, basicTextPosition);
        if (!directionConfig) {
            es.log.error("Could not get directionConfig", startingIndex, directionIndex, basicTextPosition);
            return undefined;
        }

        const isPreferredPosition =
            basicTextPosition === "BELOW" && directionConfig.direction === "S" && attemptRound === 1 ||
            basicTextPosition === "ABOVE" && directionConfig.direction === "N" && attemptRound === 1 ||
            basicTextPosition === "CENTRAL" && directionConfig.direction === "S" && attemptRound === 1;

        // calculate base padding size (using at least 7, things go a bit off otherwise)
        //const baseFeatPadding = (featRadius * this.MIN_PADDING_MULTIPLIER) * attemptRound;
        //const baseFeatPadding = (Math.max(7, featRadius) * attemptRound) * this.MIN_PADDING_MULTIPLIER;
        const baseFeaturePadding = (featRadius * attemptRound) * (isPreferredPosition? 1.2 : 2.2);

        // Get the angle in radians to determine how much each dimension contributes
        const angleRad = (directionConfig.angle * Math.PI) / 180;

        /*
            Calculate how much each dimension (width/height) contributes based on the angle.
            Angles start at East (0°) and go clockwise around the circle.

            E = 0° = 0 radians
                cos(0°) = 1 (full width contribution)
                sin(0°) = 0 (no height contribution)

            S = 90° = π/2 radians
                cos(90°) = 0 (note: ignore numbers like 6.123233995736766e-17 - floating point rounding but is basically 0)
                sin(90°) = 1 (full height contribution)

            W = 180° = π radians
                cos(180°) = -1 (but we take Math.abs val, so 1)
                sin(180°) = 0

            N = 270° = 3π/2 radians
                cos(270°) = 0
                sin(270°) = -1 (but we take Math.abs val, so 1)

            SE = 45° = π/4 radians
                cos(45°) ≈ 0.707
                sin(45°) ≈ 0.707

            e.g. SE means we use 70.07% of both width and height contributions
         */
        const widthContribution = Math.abs(Math.cos(angleRad));
        const heightContribution = Math.abs(Math.sin(angleRad));
        //es.log.log("directionConfig.direction", directionConfig.direction, widthContribution, heightContribution);

        // calculate padding based on actual label size (spacing)
        // divide by 2 since we want radius (middle of label)
        const widthComponent = (spacing.width / 2) * widthContribution;
        const heightComponent = (spacing.height / 2) * heightContribution;

        // calculate diagonal padding using Pythagorean theorem or else we'll bias towards ordinal (NE, ...) versus
        // cardinal (N, S, E, W) placement (see: https://en.wikipedia.org/wiki/Cardinal_direction)
        // const padding = baseFeatPadding + widthBasedPadding + heightBasedPadding;
        const dimensionPadding = Math.sqrt((widthComponent * widthComponent) + (heightComponent * heightComponent));
        const padding = basicTextPosition === "CENTRAL" && attemptIndex == 0 ? 0 : baseFeaturePadding + dimensionPadding;

        const rad = (directionConfig.angle * Math.PI) / 180;

        const positionDirection = {
            direction: directionConfig.direction,
            offset: {
                x: Math.cos(rad) * padding,
                y: Math.sin(rad) * padding,
            },
            attempt: attemptRound
        };
        /*
        es.log.log(
            "featRadius", featRadius,
            "attemptIndex", attemptIndex,
            "featRadius", featRadius,
            "padding", padding,
            "dimensionPadding", dimensionPadding,
            "positionDirection", positionDirection
        );
        */
        return positionDirection;
    }

    /**
     * Helper method to determine how many total positions are available given a maximum number of attempts
     */
    public static getTotalPositionCount(maxAttempts: number): number {
        return maxAttempts * this.DIRECTIONS_PER_ATTEMPT;
    }
}
